import { Component, EventEmitter, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl, FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  buildOptionsFromEnum,
  buildSelectOptionsFromEnum,
  getLtvPercentageText,
  productLengthBtlOptions,
  productLengthOptions,
} from '../../helpers';
import { RepaymentMethod, RepaymentVehicle, errorMessages } from '../../models';
import { AffordabilityService, ModalService } from '../../services';
import { Channel } from '../../models/channels';
import { MaxLtvValidatorDirective } from '../forms/directives/max-ltv-validator.directive';
import { MinPropertyValueValidatorDirective } from '../forms/directives/min-property-value-validator.directive';
import { InterestOnlyAmountValidatorDirective } from '../forms/directives/interest-only-amount-validator.directive';
import { MortgageTermValidatorDirective } from '../forms/directives/mortgage-term-validator.directive';
import { CurrencyMaskDirective } from '../forms/directives/currency-mask.directive';
import { GreaterThanValidatorDirective } from '../forms/directives/greater-than-validator.directive';

@Component({
  selector: 'app-results-edit-search',
  templateUrl: 'results-edit-search.component.html',
  styleUrls: ['./results-edit-search.component.scss'],
  imports: [
    FormsModule,
    MaxLtvValidatorDirective,
    MinPropertyValueValidatorDirective,
    InterestOnlyAmountValidatorDirective,
    MortgageTermValidatorDirective,
    CurrencyMaskDirective,
    GreaterThanValidatorDirective,
  ],
})
export class ResultsEditSearchComponent {
  private readonly affordabilityService = inject(AffordabilityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  @Output() public updated: EventEmitter<void> = new EventEmitter();
  @ViewChild('editForm', { static: true }) public editForm: NgForm;
  @ViewChild('printModalTemplateRef', { static: true }) public printModalTemplateRef: TemplateRef<unknown>;
  public window: Window;
  public printResults: boolean = false;
  public printInputs: boolean = false;

  public constructor() {
    this.window = window;
  }

  public get lendingType() {
    return this.activatedRoute.snapshot.queryParams['lendingType'] ?? ('resi' as 'resi' | 'btl');
  }

  public get channel(): Channel {
    return this.activatedRoute.snapshot.queryParams['channel'] ?? Channel.Intermediary;
  }

  public get isIntermediaryChannel(): boolean {
    return this.channel === Channel.Intermediary;
  }

  public get model() {
    return this.affordabilityService.model;
  }

  public get quoteRef() {
    return this.affordabilityService.quoteRef;
  }

  public get ltvPercentage() {
    const { propertyValue, loanAmount } = this.model.propertyAndLoan;
    return getLtvPercentageText(propertyValue, loanAmount);
  }

  public get repaymentMethods() {
    return buildSelectOptionsFromEnum(RepaymentMethod);
  }

  public get repaymentVehicles() {
    return buildOptionsFromEnum(RepaymentVehicle);
  }

  public get productLengthOptions() {
    return this.lendingType === 'btl' ? productLengthBtlOptions() : productLengthOptions();
  }

  public get affiliateCID() {
    return this.activatedRoute.snapshot.queryParams['affiliateCID'] ?? '';
  }

  public get trafficSource() {
    return this.activatedRoute.snapshot.queryParams['trafficSource'] ?? '';
  }

  public printModal() {
    this.modalService.open(this.printModalTemplateRef, {
      modalDialogClass: 'modal-lg',
      props: {
        title: 'Download my calculations',
      },
    });
  }

  public print() {
    this.modalService.close();
    window.print();
  }

  public close() {
    this.modalService.close();
  }

  public update(form: NgForm) {
    if (form.valid) {
      this.affordabilityService.form.reset(this.model);
      this.affordabilityService.getResults(this.lendingType, this.affiliateCID, this.trafficSource, this.channel)
        .subscribe(() => {
          this.updated.emit();
        });
    }
  }

  public editAll() {
    this.affordabilityService.updateModel();
    this.affordabilityService.edit = true;
    this.navigateToCalculator();
  }

  public newSearch() {
    this.affordabilityService.resetModel();
    this.affordabilityService.newSearch = true;
    this.navigateToCalculator();
  }

  public valid(control: FormControl) {
    return (control.touched || control.dirty || this.editForm.submitted) && control.valid;
  }

  public invalid(control: FormControl) {
    return (control.touched || control.dirty || this.editForm.submitted) && !control.valid;
  }

  public getErrorMessage(field: FormControl) {
    const firstError = Object.keys(field.errors)[0];
    if (!firstError) {
      return null;
    }

    const errorMessage = errorMessages[firstError];
    return errorMessage ?? firstError;
  }

  public trackByFn(index) {
    return index;
  }

  private navigateToCalculator(): void {
    this.router.navigate(['/'], {
      queryParams: {
        lendingType: this.lendingType,
        channel: this.channel,
        affiliateCID: this.affiliateCID,
        trafficSource: this.trafficSource,
      },
    });
  }
}
